/**
 * Cookie Category Toggles (framework-agnostic renderer)
 *
 * Renders the per-category toggle rows used inside the vanilla Customize
 * modal (`customizeModal.ts`). Produces plain DOM with Tailwind classes
 * that match Flowbite's `ToggleSwitch` visuals without depending on
 * React or Flowbite at runtime.
 *
 * Three rows: Strictly Necessary (always on), Analytics and Advertising.
 * Under Global Privacy Control (`gpcLocked`) the Advertising switch is
 * forced off, disabled and titled "(Locked by GPC)", like the marketing
 * site. Each switch is labelled "<title> cookies".
 *
 * Each category expands into a cookie inventory panel: name, purpose,
 * duration, first/third-party. Sentry is disclosed under Strictly
 * Necessary per the spec.
 */

import type { ConsentCategories } from "./core";

export interface CategoryToggleRenderOptions {
  values: ConsentCategories;
  onChange: (next: ConsentCategories) => void;
  disabled?: boolean;
  /** Global Privacy Control is on: Advertising is locked off. */
  gpcLocked?: boolean;
}

export interface CategoryToggleHandle {
  getValues: () => ConsentCategories;
  destroy: () => void;
}

interface CategoryDefinition {
  key: keyof ConsentCategories;
  title: string;
  required: boolean;
  description: string;
  cookies: Array<{
    name: string;
    purpose: string;
    duration: string;
    provider: string;
  }>;
}

const CATEGORIES: CategoryDefinition[] = [
  {
    key: "necessary",
    title: "Strictly Necessary",
    required: true,
    description:
      "Required for authentication, security, preserving your consent choice, honoring a browser opt-out signal such as Global Privacy Control, and error monitoring for service reliability. These cannot be turned off.",
    cookies: [
      {
        name: "as_cookie_consent",
        purpose: "Remembers your cookie preferences and version",
        duration: "12 months",
        provider: "First-party",
      },
      {
        name: "as_gpc",
        purpose:
          "Records that your browser sent a Global Privacy Control signal with this request, so the site can honor it",
        duration: "10 minutes",
        provider: "First-party",
      },
      {
        name: "as_anonymous_id",
        purpose: "Correlates pre-login activity with a later sign-in",
        duration: "12 months",
        provider: "First-party",
      },
      {
        name: "_active_snap_session",
        purpose: "Devise session cookie",
        duration: "Session",
        provider: "First-party",
      },
      {
        name: "CSRF-Token",
        purpose: "Protects against cross-site request forgery",
        duration: "Session",
        provider: "First-party",
      },
      {
        name: "Sentry (server-side)",
        purpose: "Error monitoring for service reliability",
        duration: "N/A",
        provider: "First-party",
      },
    ],
  },
  {
    key: "analytics",
    title: "Analytics",
    required: false,
    description:
      "Measures product usage in aggregate via Google Analytics, loaded through Google Tag Manager, to help us improve the app. Depending on your region, this category may already be switched on when you first arrive; switching it off here stops that measurement.",
    cookies: [
      {
        name: "_ga",
        purpose: "Google Analytics: distinguishes unique visitors",
        duration: "2 years",
        provider: "Google (third-party)",
      },
      {
        name: "_ga_<container-id>",
        purpose: "Google Analytics: persists session state",
        duration: "2 years",
        provider: "Google (third-party)",
      },
    ],
  },
  {
    key: "advertising",
    title: "Advertising",
    required: false,
    description:
      "Used for ad measurement and retargeting via Meta Pixel and Google Ads once those tags are enabled in Google Tag Manager. This category is controlled separately from Analytics and the two start independently, so depending on your region it may already be switched on before you choose. While it is off, these cookies are not set and no advertising data is shared.",
    cookies: [
      {
        name: "_fbp",
        purpose:
          "Meta Pixel: identifies browsers for ad delivery and measurement",
        duration: "90 days",
        provider: "Meta (third-party)",
      },
      {
        name: "_fbc",
        purpose:
          "Meta Pixel: stores ad click identifiers for conversion attribution",
        duration: "90 days",
        provider: "Meta (third-party)",
      },
      {
        name: "_gcl_au",
        purpose: "Google Ads: stores ad click info for conversion attribution",
        duration: "90 days",
        provider: "Google (third-party)",
      },
    ],
  },
];

/**
 * Render category toggle rows into the given container. The container
 * is emptied before rendering so this function can safely re-render on
 * state changes.
 *
 * @param container - the DOM node to render into
 * @param options - current values, onChange callback, optional disabled
 *   and GPC-lock flags
 * @returns a handle exposing the current values and a destroy function
 */
export function renderCategoryToggles(
  container: HTMLElement,
  options: CategoryToggleRenderOptions,
): CategoryToggleHandle {
  const state: ConsentCategories = { ...options.values, necessary: true };
  container.replaceChildren();

  for (const cat of CATEGORIES) {
    const locked = cat.key === "advertising" && options.gpcLocked === true;
    if (locked) state.advertising = false;

    const row = document.createElement("div");
    row.className =
      "border border-border-base rounded-lg p-4 mb-3 bg-surface-card";

    const header = document.createElement("div");
    header.className = "flex items-center justify-between gap-3";
    row.appendChild(header);

    const headingWrap = document.createElement("div");
    headingWrap.className = "flex-1";
    const title = document.createElement("h3");
    title.className = "text-base font-semibold text-text-title";
    title.textContent =
      cat.title +
      (cat.required ? " (Always On)" : "") +
      (locked ? " (Locked by GPC)" : "");
    const desc = document.createElement("p");
    desc.className = "text-sm text-text-supporting mt-1";
    desc.textContent = cat.description;
    headingWrap.appendChild(title);
    headingWrap.appendChild(desc);
    header.appendChild(headingWrap);

    const isOn = cat.required || (!locked && state[cat.key] === true);
    const isDisabled = options.disabled === true || cat.required || locked;
    const toggle = buildToggle({
      category: cat.key,
      label: cat.title,
      checked: isOn,
      disabled: isDisabled,
      onToggle: (next) => {
        if (cat.key === "necessary" || locked) return;
        state[cat.key] = next;
        options.onChange({ ...state });
      },
    });
    header.appendChild(toggle);

    const inventory = document.createElement("details");
    inventory.className = "mt-3";
    const summary = document.createElement("summary");
    summary.className =
      "text-sm font-medium text-text-teal-700 hover:text-text-teal-800 cursor-pointer select-none";
    summary.textContent = "View cookies in this category";
    inventory.appendChild(summary);

    const table = document.createElement("div");
    table.className = "mt-3 space-y-2";
    for (const c of cat.cookies) {
      const cookieRow = document.createElement("dl");
      cookieRow.className =
        "text-xs border-t border-border-faint pt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1";
      cookieRow.innerHTML = "";
      addDefRow(cookieRow, "Name", c.name);
      addDefRow(cookieRow, "Purpose", c.purpose);
      addDefRow(cookieRow, "Duration", c.duration);
      addDefRow(cookieRow, "Provider", c.provider);
      table.appendChild(cookieRow);
    }
    inventory.appendChild(table);
    row.appendChild(inventory);

    container.appendChild(row);
  }

  return {
    getValues: () => ({ ...state }),
    destroy: () => container.replaceChildren(),
  };
}

/** Track fill when on / off; shared by the initial render and the click handler. */
const TRACK_ON = " bg-fixed-teal-600";
const TRACK_OFF = " bg-surface-track-strong";
/** The knob stays white in both themes, like the `ui/Switch` knob. */
const KNOB_BASE =
  "inline-block h-5 w-5 transform rounded-full bg-overlay-light shadow transition-transform ";

interface ToggleOptions {
  category: keyof ConsentCategories;
  /** Category title; the switch's aria-label is "<label> cookies". */
  label: string;
  checked: boolean;
  disabled: boolean;
  onToggle: (next: boolean) => void;
}

/**
 * Build a single toggle-switch element styled to match Flowbite's
 * `ToggleSwitch`. Keyboard accessible (button + aria-checked) so
 * screen-reader users can perceive the state without a live region.
 *
 * @param opts - toggle config (initial state, callback, disabled flag)
 * @returns the toggle button element
 */
function buildToggle(opts: ToggleOptions): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.setAttribute("role", "switch");
  btn.setAttribute("aria-checked", String(opts.checked));
  btn.setAttribute("data-category", opts.category);
  btn.setAttribute("aria-label", `${opts.label} cookies`);
  if (opts.disabled) btn.setAttribute("aria-disabled", "true");
  btn.disabled = opts.disabled;

  const base =
    "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors " +
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-fixed-teal-500 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-card";
  btn.className =
    base +
    (opts.checked ? TRACK_ON : TRACK_OFF) +
    (opts.disabled ? " opacity-60 cursor-not-allowed" : " cursor-pointer");

  const knob = document.createElement("span");
  knob.setAttribute("aria-hidden", "true");
  knob.className =
    KNOB_BASE + (opts.checked ? "translate-x-5" : "translate-x-0.5");
  btn.appendChild(knob);

  btn.addEventListener("click", () => {
    if (opts.disabled) return;
    const next = btn.getAttribute("aria-checked") !== "true";
    btn.setAttribute("aria-checked", String(next));
    btn.className =
      base +
      (next ? TRACK_ON : TRACK_OFF) +
      (opts.disabled ? " opacity-60 cursor-not-allowed" : " cursor-pointer");
    knob.className = KNOB_BASE + (next ? "translate-x-5" : "translate-x-0.5");
    opts.onToggle(next);
  });

  return btn;
}

/**
 * Append a `<dt>`/`<dd>` pair to the cookie inventory grid.
 *
 * @param grid - the `<dl>` element to append into
 * @param label - the term displayed in the left column
 * @param value - the description displayed in the right column
 */
function addDefRow(grid: HTMLElement, label: string, value: string): void {
  const dt = document.createElement("dt");
  dt.className = "font-medium text-text-supporting";
  dt.textContent = label;
  const dd = document.createElement("dd");
  dd.className = "text-text-label break-all";
  dd.textContent = value;
  grid.appendChild(dt);
  grid.appendChild(dd);
}
